import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Send, Sparkles, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import { api, API } from "../lib/api";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function fmt(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AIChat() {
  const [pandits, setPandits] = useState([]);
  const [pricing, setPricing] = useState({});
  const [live, setLive] = useState(false);
  const [selected, setSelected] = useState(null);
  const [duration, setDuration] = useState(null);
  const [starting, setStarting] = useState(false);

  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [botTyping, setBotTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/pandits").then((r) => { setPandits(r.data.pandits); setPricing(r.data.pricing); setLive(r.data.payments_live); });
  }, []);

  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [session]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, botTyping]);

  const createSession = async (orderId, paymentId, signature) => {
    const { data } = await api.post("/chat/verify-payment", { order_id: orderId, payment_id: paymentId, signature });
    const pandit = pandits.find((p) => p.id === selected.id) || selected;
    setSession({ ...data, pandit });
    setRemaining(data.duration * 60);
    setMessages([{ role: "assistant", content: `Namaste 🙏 I am ${pandit.name}. It's a joy to connect with you. Tell me your name and date of birth, and share what's on your mind today.` }]);
  };

  const startChat = async () => {
    if (!selected || !duration) return;
    setStarting(true);
    try {
      const { data: order } = await api.post("/chat/create-order", { pandit_id: selected.id, duration });
      if (order.live) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment. Try again."); setStarting(false); return; }
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount * 100, currency: "INR",
          name: "Numoraa", description: `${selected.name} · ${duration} min`,
          order_id: order.razorpay_order_id,
          handler: async (resp) => {
            try {
              await createSession(order.order_id, resp.razorpay_payment_id, resp.razorpay_signature);
            } catch {
              toast.error("Payment verification failed. If you were charged, please contact support.");
              setStarting(false);
            }
          },
          theme: { color: "#4A154B" },
          modal: { ondismiss: () => { toast("Payment cancelled."); setStarting(false); } },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed. Please try again.");
          setStarting(false);
        });
        rzp.open();
      } else {
        // Demo mode (no Razorpay keys configured) — unlock instantly
        toast.success("Demo session unlocked ✨");
        await createSession(order.order_id, null, null);
      }
    } catch {
      toast.error("Could not start session. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  const send = async () => {
    if (!input.trim() || remaining <= 0 || botTyping) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBotTyping(true);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.session_id, message: text }),
      });
      if (!res.ok) throw new Error();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const n = [...m]; n[n.length - 1] = { role: "assistant", content: n[n.length - 1].content + payload.delta }; return n;
            });
          }
        }
      }
    } catch {
      setMessages((m) => { const n = [...m]; n[n.length - 1] = { role: "assistant", content: "I'm having trouble connecting right now. Please try again." }; return n; });
    } finally {
      setBotTyping(false);
    }
  };

  const reset = () => { setSession(null); setSelected(null); setDuration(null); setMessages([]); };

  // ---------- ACTIVE CHAT ----------
  if (session) {
    const pandit = session.pandit;
    const expired = remaining <= 0;
    return (
      <div className="pt-24 md:pt-28 pb-10 min-h-screen">
        <SEO title="AI Pandit Chat" description="Live AI numerology consultation" path="/chat" />
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between rounded-t-3xl border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img src={pandit.image} alt={pandit.name} className="h-11 w-11 rounded-full object-cover" />
                <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
              </div>
              <div>
                <p className="font-medium text-foreground">{pandit.name}</p>
                <p className="text-xs text-muted-foreground">{pandit.specialization}</p>
              </div>
            </div>
            <div className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${expired ? "bg-destructive/10 text-destructive" : "bg-gold/15 text-foreground"}`} data-testid="chat-timer">
              <Clock className="h-4 w-4" /> {fmt(remaining)}
            </div>
          </div>

          <div ref={scrollRef} className="h-[52vh] overflow-y-auto border-x border-border bg-secondary/30 p-5 no-scrollbar" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`mb-4 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-white border border-border rounded-bl-sm"}`}>
                  {m.content || (botTyping && i === messages.length - 1 ? <span className="flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-gold" /><span className="h-2 w-2 animate-bounce rounded-full bg-gold [animation-delay:0.15s]" /><span className="h-2 w-2 animate-bounce rounded-full bg-gold [animation-delay:0.3s]" /></span> : m.content)}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-b-3xl border border-border bg-card p-4">
            {expired ? (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Your session has ended. We hope it brought you clarity 🙏</p>
                <button onClick={reset} data-testid="chat-buy-more" className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm text-primary-foreground hover:shadow-gold">Purchase more time</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask your question…" className="flex-1 rounded-full border border-border bg-background px-5 py-3 text-sm outline-none focus:border-primary" data-testid="chat-input" />
                <button onClick={send} disabled={botTyping} data-testid="chat-send" className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50">{botTyping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
              </div>
            )}
          </div>
          <button onClick={reset} className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> End & choose another Pandit</button>
        </div>
      </div>
    );
  }

  // ---------- SELECTION ----------
  return (
    <div className="pt-28 md:pt-32">
      <SEO title="AI Pandit Chat" description="Chat live with Numoraa's AI Pandits — specialists in life path, love, career and Lo Shu remedies. Warm, private numerology guidance." path="/chat" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "AI Chat", path: "/chat" }]} />
        <Reveal>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter">Meet your <span className="italic text-primary">AI Pandit</span></h1>
          <p className="mt-6 max-w-xl text-lg font-light text-muted-foreground">Choose a specialist, pick your time, and enjoy a warm one-on-one numerology conversation.</p>
          {!live && <p className="mt-3 inline-block rounded-full bg-gold/15 px-4 py-1.5 text-xs text-foreground">Demo mode: sessions unlock instantly (add Razorpay keys to enable live payments).</p>}
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 pb-8">
          {pandits.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <button onClick={() => { setSelected(p); setDuration(null); }} data-testid={`pandit-${p.id}`}
                className={`group w-full h-full overflow-hidden rounded-3xl border bg-card text-left shadow-soft transition-all hover:-translate-y-1 hover:shadow-lift ${selected?.id === p.id ? "border-primary ring-2 ring-primary/20" : "border-border"}`}>
                <div className="relative h-48 overflow-hidden">
                  <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {selected?.id === p.id && <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground"><Check className="h-4 w-4" /></span>}
                </div>
                <div className="p-5">
                  <p className="text-xs uppercase tracking-[0.15em] text-gold">{p.specialization}</p>
                  <h3 className="mt-1 font-serif text-2xl">{p.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.intro}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <AnimatePresence>
          {selected && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-24 rounded-3xl border border-border bg-secondary/40 p-8" data-testid="duration-panel">
              <h2 className="font-serif text-2xl">Choose your time with {selected.name}</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-4">
                {Object.entries(pricing).map(([mins, price]) => (
                  <button key={mins} onClick={() => setDuration(Number(mins))} data-testid={`duration-${mins}`}
                    className={`rounded-2xl border p-5 text-center transition-all hover:-translate-y-0.5 ${duration === Number(mins) ? "border-primary bg-white ring-2 ring-primary/20" : "border-border bg-white"}`}>
                    <p className="font-serif text-3xl text-primary">₹{price}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{mins} minutes</p>
                  </button>
                ))}
              </div>
              <button onClick={startChat} disabled={!duration || starting} data-testid="start-session"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold disabled:opacity-50">
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-gold" />}
                {duration ? `Start ${duration}-min session · ₹${pricing[duration]}` : "Select a duration"}
              </button>
              <p className="mt-3 text-xs text-muted-foreground">AI Pandit guidance is for reflection & encouragement — never guaranteed predictions.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
